/**
 * `/content` — Content Performance (SPEC `Content Performance`, endpoints #87-#93).
 *
 * One screen, three modes, and the same shell `/view` uses: the pill header over the centered 960px
 * column in Metrics mode, the flat bar over the edge-to-edge grid in Posts and Comments. Everything
 * it decides lives in `contentState.ts`; this file is the wiring.
 *
 * Four things worth knowing:
 *
 *  - **The ui-state gate.** `content:main` loads before the first data request (§12.4), so a
 *    persisted range or granularity is never briefly ignored and no query is fired twice. Its
 *    defaults are the spec's: All time and Weekly, not the view screen's 30 days and auto-pick.
 *  - **`?mode=` beats the stored mode**, and only an explicit param does — the `ViewPage` pattern,
 *    restored once and then written through on every change.
 *  - **The screen's "has anything ever been imported?" discriminant is the session list** (#91),
 *    not `meta.hasData`: the empty island must render in all three modes, and #91 is the one cheap
 *    request every mode can afford (the import modal needs it anyway, off the same cache entry).
 *    #87 is therefore fired only in Metrics mode, and only once a session exists.
 *  - **No SSE.** Nothing here is invalidated by an event; the import and the session delete drop
 *    the whole `['content']` root from inside `ImportDialog`, and the charts and both table pages
 *    are re-read together.
 *
 * `PostPreviewProvider` is mounted for the Posts table's enriched rows: the popup's id lives in the
 * search params, so it works on this route exactly as it does on `/`, `/feed` and `/watch` — it
 * needs no `/view/:t/:id` match.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { BucketGranularity, ViewRangeState } from '@lw/shared';
import { getContentMetrics, getContentSessions } from '../../api/content';
import { QK } from '../../api/queryKeys';
import { pageTitle } from '../../app/appTitle';
import { useContentRoute } from '../../app/useContentRoute';
import { EmptyState, ErrorState, Spinner, UploadIcon, cx } from '../../components';
import { useDebouncedCallback, useDocumentTitle } from '../../lib/hooks';
import { useAppTimezone, useMediaEnabled, useTimezoneBootstrap } from '../settings/useSettings';
import { PostPreviewProvider } from '../view/PreviewContext';
import { anchorNow, resolveRange } from '../view/viewState';
import { ContentCommentsTable } from './ContentCommentsTable';
import { ContentHeader } from './ContentHeader';
import { ContentMetrics } from './ContentMetrics';
import { ContentPostsTable } from './ContentPostsTable';
import { ImportDialog } from './ImportDialog';
import {
  restoredContentMode,
  toContentCommentsParams,
  toContentMetricsParams,
  toContentPostsParams,
  type ContentCommentsSort,
  type ContentPostsSort,
} from './contentState';
import { useContentUiState } from './useContentUiState';
import '../view/view.css';
import './content.css';

const SEARCH_DEBOUNCE_MS = 300;

export function ContentPage() {
  useTimezoneBootstrap();
  useDocumentTitle(pageTitle('Content Performance'));

  const timezone = useAppTimezone();
  const mediaEnabled = useMediaEnabled();

  const { mode, setMode, hasExplicitMode } = useContentRoute();
  const uiState = useContentUiState();
  const [importOpen, setImportOpen] = useState(false);

  /* ------------------------------------------------------------- mode sync */

  const modeRestored = useRef(false);
  useEffect(() => {
    if (!uiState.ready || modeRestored.current) return;
    modeRestored.current = true;
    const restored = restoredContentMode({
      urlMode: mode,
      hasExplicitMode,
      storedMode: uiState.state.mode,
    });
    if (restored !== null) setMode(restored);
  }, [uiState.ready, uiState.state.mode, mode, hasExplicitMode, setMode]);

  useEffect(() => {
    if (!uiState.ready || !modeRestored.current) return;
    if (uiState.state.mode !== mode) uiState.update({ mode });
  }, [mode, uiState]);

  /* ---------------------------------------------------------- range/search */

  // Re-anchored only when the range selection changes, so query keys do not churn per render.
  const rangeKey = `${uiState.state.range.preset}|${uiState.state.range.from ?? ''}|${uiState.state.range.to ?? ''}`;
  const now = useMemo(() => anchorNow(), [rangeKey]);
  const resolvedRange = useMemo(() => resolveRange(uiState.state.range, now), [rangeKey, now]);

  // Session-only, like `/watch`: two states so the field stays responsive while the query key
  // moves every 300 ms. Not in the stored blob — a search term is not a saved view.
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const commitSearch = useDebouncedCallback(
    (next: string) => setCommittedSearch(next),
    SEARCH_DEBOUNCE_MS,
  );

  const postsSortKey = `${uiState.state.postsSort.key}|${uiState.state.postsSort.dir}`;
  const commentsSortKey = `${uiState.state.commentsSort.key}|${uiState.state.commentsSort.dir}`;

  const metricsParams = useMemo(
    () => toContentMetricsParams(uiState.state, resolvedRange),
    [uiState.state.granularity, resolvedRange],
  );
  const postsParams = useMemo(
    () => toContentPostsParams(uiState.state, resolvedRange, committedSearch),
    [postsSortKey, resolvedRange, committedSearch],
  );
  const commentsParams = useMemo(
    () => toContentCommentsParams(uiState.state, resolvedRange, committedSearch),
    [commentsSortKey, resolvedRange, committedSearch],
  );

  /* --------------------------------------------------------------- queries */

  const sessionsQuery = useQuery({
    queryKey: QK.contentSessions(),
    queryFn: ({ signal }) => getContentSessions(signal),
    enabled: uiState.ready,
  });
  const sessions = sessionsQuery.data?.sessions ?? [];
  const hasImports = sessions.length > 0;

  const metricsQuery = useQuery({
    queryKey: QK.contentMetrics(metricsParams),
    queryFn: ({ signal }) => getContentMetrics(metricsParams, signal),
    enabled: uiState.ready && mode === 'metrics' && hasImports,
    // Changing granularity or range re-reads everything; the previous charts stay up meanwhile.
    placeholderData: keepPreviousData,
  });
  const meta = metricsQuery.data?.meta;

  /* ---------------------------------------------------------------- render */

  const isTable = mode !== 'metrics';
  const openImport = () => setImportOpen(true);

  const setRange = (range: ViewRangeState) => uiState.update({ range });
  const setGranularity = (granularity: BucketGranularity) => uiState.update({ granularity });
  const setPostsSort = (postsSort: ContentPostsSort) => uiState.update({ postsSort });
  const setCommentsSort = (commentsSort: ContentCommentsSort) => uiState.update({ commentsSort });

  const changeSearch = (next: string) => {
    setSearch(next);
    commitSearch(next);
  };

  const clearNarrowing = () => {
    setSearch('');
    setCommittedSearch('');
    commitSearch.cancel();
    if (uiState.state.range.preset !== 'all') setRange({ preset: 'all' });
  };

  // What could be hiding rows: a search term, or a range narrower than everything.
  const narrowed = committedSearch.trim().length > 0 || uiState.state.range.preset !== 'all';

  // The fullscreen grid modes have no sub-row, so the footer states when the data last changed —
  // the analogue of `/view`'s "Last scraped …". The date itself is the server's spelling.
  const newest = sessions[0];
  const footerLeft = newest === undefined ? '' : `Last import ${newest.importedAtDisplay}`;

  const booting = !uiState.ready || sessionsQuery.isPending;
  const showStates = booting || sessionsQuery.isError || uiState.error !== null || !hasImports;

  return (
    <PostPreviewProvider mediaEnabled={mediaEnabled} timezone={timezone}>
      <div className={cx('view', isTable && 'view--posts')}>
        <ContentHeader
          mode={mode}
          onModeChange={setMode}
          state={uiState.state}
          resolvedRange={resolvedRange}
          onRangeChange={setRange}
          onGranularityChange={setGranularity}
          effectiveGranularity={meta?.granularity}
          summaryDisplay={meta?.summaryDisplay}
          search={search}
          onSearchChange={changeSearch}
          timezone={timezone}
          now={now}
          onImport={openImport}
          postsParams={postsParams}
          commentsParams={commentsParams}
        />

        {showStates ? (
          <div className="page view-body">
            <div className="content">
              {uiState.error !== null ? (
                <ErrorState
                  title="Couldn't restore your saved view"
                  description="The screen is showing All time at weekly granularity. Retrying re-reads what was stored."
                  onRetry={uiState.retry}
                />
              ) : null}

              {sessionsQuery.isError ? (
                <ErrorState
                  title="Couldn't load your imports"
                  error={sessionsQuery.error}
                  onRetry={() => void sessionsQuery.refetch()}
                  retrying={sessionsQuery.isFetching}
                />
              ) : null}

              {booting ? <Spinner size="lg" label="Loading content performance…" /> : null}

              {!booting && sessionsQuery.isSuccess && !hasImports ? (
                <EmptyState
                  title="No content data yet"
                  description="Import your LinkedIn analytics export."
                  action={
                    <button type="button" className="btn-primary" onClick={openImport}>
                      <UploadIcon />
                      Import
                    </button>
                  }
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {!showStates && mode === 'posts' ? (
          <ContentPostsTable
            params={postsParams}
            mediaEnabled={mediaEnabled}
            sort={uiState.state.postsSort}
            onSortChange={setPostsSort}
            enabled={uiState.ready}
            narrowed={narrowed}
            onClear={clearNarrowing}
            footerLeft={footerLeft}
          />
        ) : null}

        {!showStates && mode === 'comments' ? (
          <ContentCommentsTable
            params={commentsParams}
            sort={uiState.state.commentsSort}
            onSortChange={setCommentsSort}
            enabled={uiState.ready}
            narrowed={narrowed}
            onClear={clearNarrowing}
            footerLeft={footerLeft}
          />
        ) : null}

        {!showStates && mode === 'metrics' ? (
          <div className="page view-body">
            <div className="content">
              {metricsQuery.isError ? (
                <ErrorState
                  title="Couldn't load the metrics"
                  error={metricsQuery.error}
                  onRetry={() => void metricsQuery.refetch()}
                  retrying={metricsQuery.isFetching}
                />
              ) : null}

              {metricsQuery.isPending ? <Spinner size="lg" label="Loading metrics…" /> : null}

              <ContentMetrics metrics={metricsQuery.data} />
            </div>
          </div>
        ) : null}

        <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
      </div>
    </PostPreviewProvider>
  );
}

export default ContentPage;
